import { useState } from "react";
import { BookOpenCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { DataTable, FinanceCard, NativeSelect, money, todayIso } from "./financeUi";

const blankLine = () => ({ account_id: "", debit: "", credit: "", narration: "" });

export default function FinanceJournal({ accounts, vouchers, reload }) {
  const [date, setDate] = useState(todayIso());
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState([blankLine(), blankLine()]);
  const setLine = (i, patch) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const totalDebit = lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.01;

  const post = async () => {
    try {
      await api.post("/admin/company-finance/journals", { date, narration, lines: lines.filter((l) => l.account_id) });
      toast.success("Journal posted");
      setLines([blankLine(), blankLine()]);
      setNarration("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post journal");
    }
  };

  return (
    <div className="space-y-5">
      <FinanceCard testid="journal-form" title="Journal entry">
        <div className="grid gap-3 md:grid-cols-[200px_1fr]">
          <Input data-testid="journal-date-input" onChange={(e) => setDate(e.target.value)} type="date" value={date} />
          <Input data-testid="journal-narration-input" onChange={(e) => setNarration(e.target.value)} placeholder="Narration" value={narration} />
        </div>
        <div className="space-y-2">
          {lines.map((line, i) => (
            <div className="grid gap-2 md:grid-cols-[1.6fr_.8fr_.8fr_1.2fr_auto]" data-testid={`journal-line-${i}`} key={i}>
              <NativeSelect data-testid={`journal-line-account-${i}`} onChange={(e) => setLine(i, { account_id: e.target.value })} value={line.account_id}>
                <option value="">Account…</option>
                {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
              </NativeSelect>
              <Input data-testid={`journal-line-debit-${i}`} onChange={(e) => setLine(i, { debit: e.target.value, credit: "" })} placeholder="Debit" type="number" value={line.debit} />
              <Input data-testid={`journal-line-credit-${i}`} onChange={(e) => setLine(i, { credit: e.target.value, debit: "" })} placeholder="Credit" type="number" value={line.credit} />
              <Input data-testid={`journal-line-narration-${i}`} onChange={(e) => setLine(i, { narration: e.target.value })} placeholder="Line note" value={line.narration} />
              <Button data-testid={`journal-line-remove-${i}`} disabled={lines.length <= 2} onClick={() => setLines(lines.filter((_, idx) => idx !== i))} size="icon" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button data-testid="journal-line-add" onClick={() => setLines([...lines, blankLine()])} size="sm" variant="outline"><Plus className="w-4 h-4" />Add line</Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm" data-testid="journal-totals">
            Debit <strong>{money(totalDebit)}</strong> · Credit <strong>{money(totalCredit)}</strong> ·{" "}
            <span className={balanced ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{balanced ? "Balanced" : "Not balanced"}</span>
          </div>
          <Button data-testid="journal-save-button" disabled={!balanced} onClick={post}><BookOpenCheck className="w-4 h-4" />Post Journal</Button>
        </div>
      </FinanceCard>
      <DataTable
        columns={[
          ["Voucher", (v) => <span className="font-mono text-xs">{v.voucher_number}</span>],
          ["Date", (v) => v.date],
          ["Type", (v) => <span className="text-xs rounded bg-muted px-2 py-0.5">{v.voucher_type}</span>],
          ["Narration", (v) => v.narration || "—"],
          ["Lines", (v) => v.lines.map((l) => `${l.account_name} ${l.debit ? "Dr " + money(l.debit) : "Cr " + money(l.credit)}`).join(" · ")],
          ["Total", (v) => money(v.total), "right"],
        ]}
        empty="No vouchers posted yet."
        rowKey={(v) => v.voucher_id}
        rows={vouchers}
        testid="voucher-table"
      />
    </div>
  );
}
